import React, { useEffect, useState } from 'react';
import { Bell, ExternalLink, Plus, RefreshCw, Save, Trash2, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, fmtDate } from '../../../lib/adminApi';

const EMPTY = { title: '', body: '', link_path: '', link_label: '', dismiss_label: '' };

export const WhatsNewHubTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.whatsNewList();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault?.();
    if (!draft.title.trim() || !draft.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.whatsNewCreate({
        title: draft.title.trim(),
        body: draft.body.trim(),
        link_path: draft.link_path.trim() || null,
        link_label: draft.link_label.trim() || null,
        dismiss_label: draft.dismiss_label.trim() || null,
      });
      toast.success('Banner created');
      setDraft(EMPTY);
      setShowCreate(false);
      load();
    } catch (err) {
      toast.error(err.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (b) => {
    try {
      await adminApi.whatsNewUpdate(b.id, { is_active: !b.is_active });
      toast.success(b.is_active ? 'Banner deactivated' : 'Banner activated');
      load();
    } catch (err) {
      toast.error(err.message || 'Update failed');
    }
  };

  const remove = async (b) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete banner "${b.title}"? Users who already dismissed it stay dismissed.`)) return;
    try {
      await adminApi.whatsNewDelete(b.id);
      toast.success('Banner deleted');
      load();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
  };

  return (
    <div data-testid="tab-whats-new" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
            <Bell className="w-4 h-4 text-fuchsia-500" /> What's New Banners
          </div>
          <div className="text-xs text-slate-500">Announce new features. Users see each banner until they tap "Got it".</div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-300 hover:bg-slate-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="whats-new-create-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-fuchsia-600 text-white hover:bg-fuchsia-700"
          >
            <Plus className="w-3.5 h-3.5" /> New Banner
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-slate-500 py-6">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            No banners yet. Create one to spotlight a new feature.
          </div>
        )}
        {!loading && items.map((b) => (
          <div key={b.id} className={`rounded-xl border bg-white p-4 ${b.is_active ? 'border-fuchsia-200/80' : 'border-slate-200 opacity-75'}`} data-testid={`whats-new-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-sm font-bold text-slate-900">{b.title}</div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${b.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {b.is_active ? 'Active' : 'Inactive'}
                  </span>
                  {typeof b.dismissed_count === 'number' && (
                    <span className="text-[10px] text-slate-400">{b.dismissed_count} dismissals</span>
                  )}
                </div>
                <div className="text-sm text-slate-600 whitespace-pre-wrap mt-1.5">{b.body}</div>
                {b.link_path && (
                  <div className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> {b.link_label || 'Check it out'} → <code className="ml-1 bg-slate-100 px-1.5 py-0.5 rounded">{b.link_path}</code>
                  </div>
                )}
                <div className="text-[11px] text-slate-400 mt-2">
                  Created {fmtDate(b.created_at)}
                  {b.expires_at && (() => {
                    const now = new Date();
                    const exp = new Date(b.expires_at);
                    const isExpired = exp <= now;
                    const daysLeft = Math.ceil((exp - now) / 86400000);
                    return (
                      <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${isExpired ? 'bg-slate-200 text-slate-600' : daysLeft <= 7 ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-slate-50 text-slate-600 border border-slate-200'}`}>
                        {isExpired ? `expired ${fmtDate(b.expires_at)}` : `expires in ${daysLeft}d (${fmtDate(b.expires_at)})`}
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button onClick={() => toggle(b)} title={b.is_active ? 'Deactivate' : 'Activate'} className="p-1.5 rounded hover:bg-slate-100">
                  {b.is_active ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
                </button>
                <button onClick={() => remove(b)} title="Delete" className="p-1.5 rounded text-rose-500 hover:bg-rose-50">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-1.5"><Bell className="w-4 h-4 text-fuchsia-500" /> Create What's New Banner</h3>
              <button onClick={() => setShowCreate(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Title</label>
                <input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. 5-Star reviews just got AI summaries"
                  data-testid="whats-new-title"
                  autoFocus
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Body</label>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={4}
                  placeholder="Briefly describe the new feature…"
                  data-testid="whats-new-body"
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 resize-y"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Link Path (optional)</label>
                  <input
                    value={draft.link_path}
                    onChange={(e) => setDraft({ ...draft, link_path: e.target.value })}
                    placeholder="/five-star"
                    data-testid="whats-new-link-path"
                    className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Link Label</label>
                  <input
                    value={draft.link_label}
                    onChange={(e) => setDraft({ ...draft, link_label: e.target.value })}
                    placeholder="Try it now"
                    className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Dismiss Button Label (optional)</label>
                <input
                  value={draft.dismiss_label}
                  onChange={(e) => setDraft({ ...draft, dismiss_label: e.target.value })}
                  placeholder='Defaults to "Got it"'
                  data-testid="whats-new-dismiss-label"
                  className="w-full px-3 py-2 text-sm rounded-md border border-slate-300 focus:outline-none focus:border-fuchsia-400"
                />
                <div className="text-[10px] text-slate-400 mt-1">Use something like <em>I understand</em> for acknowledgement-style banners.</div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-3 py-2 text-sm font-semibold rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={saving} data-testid="whats-new-save" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md bg-fuchsia-600 text-white hover:bg-fuchsia-700 disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Publish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
